import { brandSchema, IBrand, IMarket, marketSchema } from 'data/schema';
import { isDebugMode, testDataRequested } from 'utils/ui/debug';
import { getStorageItem } from 'utils/ui/storage';
import { isDealerIdFormatValid } from './dealerIdFormat';

// Merged on every stage when `?test=` is present (AVS-4406 AC7: ordinary list members, no bypass).
export const BASE_TEST_DEALER_IDS = ['DEU00100V', 'DEU00100L'] as const;
// Merged only outside prod; covers the brand/market combos visited by the E2E suites (V/ESP stays uncovered).
export const EXTENDED_TEST_DEALER_IDS = [
  'DEU00100A',
  'DEU00100N',
  'FRA00100A',
  'FRA00100N',
  'GBR00100A',
  'GBR00100V',
  'AUT00100A',
  'AUT00100V',
  'CHE00100A',
  'CHE00100N',
  'POL00100A',
  'NLD00100N',
  'ITA00100L',
] as const;
// F2: positive allowlist for the extended set; anything else (prod, unknown, unset) stays base-only.
const EXTENDED_TEST_DEALER_ID_STAGES: readonly string[] = ['test', 'dev', 'prelive'];

export interface IUserInfo {
  type: 'dealer' | 'customer';
  brand: IBrand;
  brands: IBrand[];
  market: {
    iso2: string;
    iso3: IMarket;
  };
  groups: string[];
  email: string;
  name: string;
  phoneNumber: string;
  publicPicture: string;
  dealerIdKvps: string;
  userPseudonym: string;
  dealerIdsList: string[];
  markets: string[];
}

export interface IUserConfig {
  getAccessTokenCallback: null | (() => string);
  getIdTokenCallback: null | (() => string);
  getUserInfoCallback: null | ((idToken?: string) => IUserInfo | null | undefined);
}

export class User {
  static config: IUserConfig = {
    getAccessTokenCallback: null,
    getIdTokenCallback: null,
    getUserInfoCallback: null,
  };

  getAccessToken() {
    return User.config.getAccessTokenCallback?.();
  }

  getIdToken() {
    return User.config.getIdTokenCallback?.();
  }

  getUserInfo() {
    const idToken = User.config.getIdTokenCallback?.();
    const userInfo = User.config.getUserInfoCallback?.(idToken);

    return userInfo;
  }

  get isDealer() {
    return this.type === 'dealer';
  }

  get type() {
    return this.getUserInfo()?.type ?? 'customer';
  }
  get brand(): IBrand {
    const stored = getStorageItem('sessionStorage', 'auth:brand');
    const parsed = brandSchema.safeParse(stored);
    if (parsed.success) {
      return parsed.data;
    }
    return this.getUserInfo()?.brand ?? 'A';
  }
  get brands(): IBrand[] {
    return this.getUserInfo()?.brands ?? [];
  }
  get market(): IUserInfo['market'] {
    const storedMarket = getStorageItem('sessionStorage', 'auth:market');
    if (typeof storedMarket === 'string') {
      const parsed = marketSchema.safeParse(storedMarket);
      if (parsed.success) {
        return { iso2: storedMarket.slice(0, 2).toLowerCase(), iso3: parsed.data };
      }
    }
    return this.getUserInfo()?.market ?? { iso2: 'de', iso3: 'DEU' as IMarket };
  }
  get groups() {
    return this.getUserInfo()?.groups ?? [];
  }
  get email() {
    return this.getUserInfo()?.email ?? '';
  }
  get name() {
    return this.getUserInfo()?.name ?? '';
  }
  get phoneNumber() {
    return this.getUserInfo()?.phoneNumber ?? '';
  }
  get publicPicture() {
    return this.getUserInfo()?.publicPicture ?? '';
  }
  get markets(): string[] {
    return this.getUserInfo()?.markets ?? [];
  }

  // The first/primary dealer ID from the list of dealer IDs.
  get dealerIdKvps() {
    return this.getUserInfo()?.dealerIdKvps ?? '';
  }
  get userPseudonym() {
    const userInfo = this.getUserInfo();
    // Fallback to dealerIdKvps when userPseudonym is not provided or empty
    // This ensures web component integrations (like LCC) that don't provide userPseudonym
    // can still authenticate using their dealer ID as a unique identifier
    return userInfo?.userPseudonym || userInfo?.dealerIdKvps || '';
  }

  // All dealer IDs for this user
  get dealerIdsList() {
    const userDealerIds = this.getUserInfo()?.dealerIdsList ?? [];
    const testDealerIds = this.getTestDealerIds();

    // Combine and deduplicate dealer IDs
    const allDealerIds = [...new Set([...userDealerIds, ...testDealerIds])];

    // then filter for valid format
    return allDealerIds.filter(this.isValidDealerIdFormat);
  }

  private getTestDealerIds(): readonly string[] {
    if (!testDataRequested()) return [];
    // F2: read STAGE strictly, no `|| 'dev'` default (unlike `utils/ui/stage.ts`'s EXHIBITION_STAGE),
    // so an unset/unknown stage is refused rather than treated as 'dev' — same fail-open pitfall
    // `telemetry/recorder/gate.ts` documents and fixes with a strict read + positive allowlist.
    const stage = process.env.STAGE;
    return typeof stage === 'string' && EXTENDED_TEST_DEALER_ID_STAGES.includes(stage)
      ? [...BASE_TEST_DEALER_IDS, ...EXTENDED_TEST_DEALER_IDS]
      : BASE_TEST_DEALER_IDS;
  }

  private isValidDealerIdFormat(id: string): boolean {
    return isDealerIdFormatValid(id);
  }

  // This is the selected dealer ID. Some users have multiple and this one holds the most recently selected
  get dealerId(): string {
    const stored = getStorageItem('sessionStorage', 'auth:dealerId');
    return typeof stored === 'string' ? stored : this.dealerIdKvps;
  }

  // The dealer ID committed for this login, or '' when none was. Unlike `dealerId` it never falls
  // back to `dealerIdKvps`, so the standalone gate can tell "not selected yet" from "selected".
  get selectedDealerId(): string {
    const stored = getStorageItem('sessionStorage', 'auth:dealerId');
    return typeof stored === 'string' ? stored : '';
  }
}

export const user = new User();

if (isDebugMode()) {
  // @ts-expect-error - expose config in global scope for debugging
  window.user = user;
}
